import { request } from './request';

// 选择器选项
export interface SelectorOption {
  id: number;
  name: string;
}

// 标签
export interface Tag {
  id: number;
  name: string;
}

// 猎头公司信息
export interface HeadhuntingCompanyInfo {
  id: number;
  enterpriseId: number;
  name: string;
  address: string;
  logoUrl: string;
}

// 企业猎头记录
export interface EnterpriseHeadhuntingRecord {
  id: number;
  enterpriseId: number;
  headhuntingCompanyId: number;
  contact: string;
  contactEmail: string;
  remark: string;
  tags: Tag[];
  headhuntingCompanyInfo: HeadhuntingCompanyInfo;
}

// 分页响应
export interface IPageObject<T = any> {
  records: T[];
  current: number;
  total: number;
  pages: number;
}

// 全部选择器响应
export interface AllSelectorsResponse {
  headhuntingCompanySeletor: SelectorOption[];
  contactSeletor: string[];
  contactEmailSeletor: SelectorOption[];
  tagSeletor: SelectorOption[];
}

// 企业猎头分页请求参数
export interface EnterpriseHeadhuntingPageRequest {
  headhuntingCompanyId?: number[];
  contact?: number[];
  contactEmail?: number[];
  tags?: number[];
  pageNum?: number;
  pageSize?: number;
  sortColumn?: string;
  asc?: boolean;
}

/**
 * 获取所有选择器数据
 */
export function getHeadhuntingCompanySelectors() {
  return request<SelectorOption[]>({
    url: '/id-enterprise/api/v1/dim/enterprise-headhunting/company-seletor',
    method: 'GET'
  });
}
/**
 * 获取所有选择器数据
 */
export function getAllSelectors() {
  return request<AllSelectorsResponse>({
    url: '/id-enterprise/api/v1/dim/enterprise-headhunting/page-all-seletor',
    method: 'GET'
  });
}

/**
 * 获取企业猎头分页列表
 * @param data 查询参数
 */
export function getEnterpriseHeadhuntingPage(data: EnterpriseHeadhuntingPageRequest) {
  return request<IPageObject<EnterpriseHeadhuntingRecord>>({
    url: '/id-enterprise/api/v1/enterprise-headhunting/page',
    method: 'POST',
    data
  });
}

// 职位对猎头开放请求参数
export interface JobHeadhuntingOpenRequest {
  jobIds: number[];
  headhuntingCompanyIds: number[];
}

/**
 * 职位对猎头开放
 * 可单职位、批量对代理开放或关闭，如果传入职位信息为单个职位ID，则覆盖，如果传入职位信息为多个，则追加
 * @param data 请求参数
 */
export function jobHeadhuntingOpen(data: JobHeadhuntingOpenRequest) {
  return request({
    url: '/ats-job/api/v1/job-info/headhunting-open',
    method: 'PUT',
    data
  });
}

// 更新企业猎头信息请求参数
export interface UpdateEnterpriseHeadhuntingRequest {
  id: number;
  enterpriseId: number;
  headhuntingCompanyId: number;
  contact: string;
  contactEmail: string;
  remark: string;
  tags: string[];
}

/**
 * 更新企业猎头信息
 * @param data 请求参数
 */
export function updateEnterpriseHeadhunting(data: UpdateEnterpriseHeadhuntingRequest) {
  return request({
    url: '/id-enterprise/api/v1/enterprise-headhunting/update',
    method: 'PUT',
    data
  });
}

// 猎头公司关联的企业记录
export interface HeadhuntingCompanyEnterpriseRecord {
  /** 关联关系ID */
  id: number;
  /** 猎头公司ID */
  headhuntingCompanyId: number;
  /** 企业ID */
  enterpriseId: number;
  /** 企业名称 */
  enterpriseName: string;
  /** 公司地址 */
  companyAddress: string;
  /** 勤務先数 */
  affiliationCount: number;
  /** 联系人(名字) */
  contact: string;
  /** 联系人邮箱 */
  contactEmail: string;
  /** 标签(列表) */
  tags: Record<string, unknown>[];
  /** 备注 */
  remark: string;
  /** logo的S3存储Key */
  logoS3key: string;
  /** logo的完整URL */
  enterpriseLogoUrl: string;
}

// 猎头公司企业全部选择器响应
export interface HeadhuntingCompanyEnterpriseSelectorsResponse {
  /** 企业选择器 */
  enterpriseSeletor: SelectorOption[];
  /** 联系人选择器 */
  contactSeletor: string[];
  /** 联系人邮箱选择器 */
  contactEmailSeletor: string[];
  /** 标签选择器 */
  tagSeletor: SelectorOption[];
}

// 猎头公司企业分页请求参数
export interface HeadhuntingCompanyEnterprisePageRequest {
  /** 页码 */
  pageNum: number;
  /** 每页数量 */
  pageSize: number;
  /** 排序字段 */
  sortColumn?: string;
  /** 猎头公司ID */
  headhuntingCompanyId?: number;
  /** 企业ID列表 */
  enterpriseIds?: number[];
  /** 联系人列表 */
  contact?: string[];
  /** 联系人邮箱列表 */
  contactEmail?: string[];
  /** 标签列表 */
  tags?: number[];
  /** 是否升序排序 */
  asc?: boolean;
}
export interface HeadhuntingJobListSelectors {
  enterpriseList: SelectorOption[];
  jobTypeList: SelectorOption[];
  jobAffiliationList: SelectorOption[];
  jobIdList: number[];
  jobIndustryPositionList: SelectorOption[];
  jobEmploymentFormList: SelectorOption[];
}
/**
 * 获取猎头公司关联企业的全部选择器数据
 * @param data 查询参数
 */
export function getHeadhuntingCompanyEnterpriseAllSelectors(data: HeadhuntingCompanyEnterprisePageRequest) {
  return request<HeadhuntingCompanyEnterpriseSelectorsResponse>({
    url: '/id-enterprise/api/v1/headhunting-company/enterprise/page-all-seletor',
    method: 'GET',
    params: data
  });
}

/**
 * 获取猎头公司关联的企业分页列表
 * @param data 查询参数
 */
export function getHeadhuntingCompanyEnterprisePage(data: HeadhuntingCompanyEnterprisePageRequest) {
  return request<IPageObject<HeadhuntingCompanyEnterpriseRecord>>({
    url: '/id-enterprise/api/v1/headhunting-company/enterprise/page',
    method: 'POST',
    data
  });
}

// 猎头职位列表分页请求参数
export interface HeadhuntingJobPageRequest {
  /** 页码 */
  pageNum: number;
  /** 每页数量 */
  pageSize: number;
  /** 排序字段 */
  sortColumn?: string;
  /** 企业ID */
  enterpriseId?: number;
  /** 企业ID列表 */
  enterpriseIdList?: number[];
  /** 职位标题 */
  jobTitle?: string;
  /** 在留资格列表 */
  jobTypeList?: number[];
  /** 勤務先ID列表 */
  affiliationIdList?: number[];
  /** 职位ID列表 */
  jobIdList?: number[];
  /** 岗位类型列表 */
  positionTypeList?: number[];
  /** 职位开放日期起始 */
  jobOpenDateStart?: string;
  /** 职位开放日期结束 */
  jobOpenDateEnd?: string;
  /** 雇佣形式列表 */
  employmentFormList?: number[];
  /** 对外公开状态列表 */
  jobOutOpenList?: number[];
  /** 是否升序排序 */
  asc?: boolean;
}

// 猎头职位记录
export interface HeadhuntingJobRecord {
  /** 职位ID */
  jobId: number;
  /** 招聘企业ID */
  enterpriseId: number;
  /** 招聘企业名称 */
  enterpriseName: string;
  /** 职位标题 */
  title: string;
  /** 岗位类型 */
  positionType: string;
  /** 是否急聘(0/1) */
  isUrgent: number;
  /** 在留资格 */
  jobType: string;
  /** 雇佣形式(1:正式员工, 2:合同员工, 3:兼职, 4:其他) */
  employmentForm: string;
  /** 勤務先ID */
  affiliation: string;
  /** 招聘人数 */
  headcount: number;
  /** 推荐人数 */
  recommendCount: number;
  /** 入职人数 */
  entryCount: number;
  /** 是否外开(0/1) */
  outOpen: number;
  /** 职位开放时间 */
  openTime: Record<string, unknown>;
  /** 创建时间 */
  createTime: Record<string, unknown>;
  /** 更新时间 */
  updateTime: Record<string, unknown>;
  /** 创建人名称 */
  creator: string;
}

/**
 * 获取猎头职位分页列表
 * @param data 查询参数
 */
export function getHeadhuntingJobPage(data: HeadhuntingJobPageRequest) {
  return request<IPageObject<HeadhuntingJobRecord>>({
    url: '/ats-job/api/v1/job-info/headhunting-job/page',
    method: 'POST',
    data
  });
}
/**
 * 获取猎头职位分页列表筛选项
 * @param data 查询参数
 */
export function getHeadhuntingPageSelectors() {
  return request<HeadhuntingJobListSelectors>({
    url: '/ats-job/api/v1/job-info/headhunting-job/page-all-seletors',
    method: 'POST',
  });
}
// 更新猎头公司企业标签和备注请求参数
export interface UpdateHeadhuntingCompanyEnterpriseTagRemarkRequest {
  /** 企业ID */
  enterpriseId: number;
  /** 标签数组 */
  tags: string[];
  /** 备注 */
  remark: string;
}

/**
 * 更新猎头公司企业标签和备注
 * @param data 请求参数
 */
export function updateHeadhuntingCompanyEnterpriseTagRemark(data: UpdateHeadhuntingCompanyEnterpriseTagRemarkRequest) {
  return request({
    url: '/id-enterprise/api/v1/headhunting-company/enterprise/tag-remark',
    method: 'PUT',
    data
  });
}



/**
 * 检查猎头邮箱
 * @param data 查询参数
 */
export function checkAgentEmail(data: {headhuntingCompanyId: number,contactEmail: string}) {
  return request<boolean>({
    url: '/id-enterprise/api/v1/enterprise-headhunting/check/email',
    method: 'POST',
    data
  });
}